import { BusinessLocation } from '../types/business-types';
import { Review } from '../types/types';

/** Build the prompt for generating a single reply to a review */
export const buildReviewReplyPrompt = (
  review: Review,
  guidelines?: string,
  priorityKeywords?: string[],
): string => {
  return `
You are a professional review responder.

Task:
Write ONLY a single, concise reply message (no explanations, no intros like "Here's a response").
The reply must:
- Be grammatically correct and polite.
- Address the specific points raised in the Google review below.
- Avoid adding opinions, meta-comments, or filler text.
- Use humor or empathy only if natural for the tone.
- Keep language appropriate for the business and target audience.
- If the review is empty or generic (no specifics), reply with a very short thank-you (max 20 words) and no assumptions.
- Output only the final message text — nothing else.

Brand/voice guidelines (apply if provided. Must take precedence over other instructions):
"""
${(guidelines || '').trim()}
"""

Priority keywords to reinforce only when naturally relevant: ${
    priorityKeywords && priorityKeywords.length
      ? priorityKeywords.join(', ')
      : 'none provided'
  }

Review: """${review.comment || ''}"""
`;
};

export const experimentalBuildReviewReplyPrompt = (
  review: Review,
  location?: BusinessLocation,
  guidelines?: string,
  priorityKeywords?: string[],
): string => {
  return `
You are the official review responder for this business. Reply in the review's language.

Goal: Improve trust, local SEO, and listing quality through clear, personalized replies—without sounding forced. Location names are optional and should only be used when they fit naturally or are referenced by the reviewer.

Task: Write ONLY a single, concise reply (no explanations). Structure: acknowledge + thank + address 1–2 specifics + close/invite back.

Rules:
- Be on-brand, polite, and brief (aim 30–60 words; if the review is very short or empty, keep it ≤20 words).
- Address concrete points from the review; do not invent details.
- If negative/neutral: acknowledge, apologize if applicable, and offer a contact path; avoid promises or compensation.
- If positive: reinforce praised specifics; invite back.
- If empty or generic rating: output a very short thank-you only (≤20 words); no assumptions about the experience. Do not mention business or location in this case.
- Maintain language parity with the reviewer.
- Light SEO: naturally reuse business names and mentioned services/keywords when relevant (no stuffing). Location names are optional; include only when it reads naturally or the reviewer mentions it.
- No meta-text, disclaimers, or placeholders. Output only the final reply text.

Brand/voice guidelines (apply if provided; override other tone choices):
"""
${(guidelines || '').trim()}
"""

Context:
- Business name (if provided): ${location?.title || ''}
- Review rating: ${review?.starRating || ''}
- Priority keywords (use only if relevant and natural): ${
    priorityKeywords && priorityKeywords.length
      ? priorityKeywords.join(', ')
      : 'none provided'
  }
- Review text:
"""${review.comment || ''}"""
`;
};
